import { lazy, Suspense, useEffect, useState, type JSX } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { Layout } from '@/widgets/Layout/ui/Layout';
import { useTelegram } from '@/shared/hooks/useTelegram';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { loginTelegram } from '@/features/auth/model/auth.thunk';
import { FullScreenLoader } from '@/shared/ui/FullScreenLoader';
import { checkEventPaymentStatus } from '@/features/events/model/events.thunk';
import { checkGoldPaymentStatus } from '@/features/buyGold/model/buyGold.thunk';
import { toast } from 'sonner';
const BookingIsPaidPage = lazy(() => import('@/pages/PaidBookingPage/ui/PaidBookingPage'));
const RegistrationPage = lazy(() => import('@/pages/RegistrationPage/ui/RegistrationPage'));
const MainPage = lazy(() => import('@/pages/MainPage/ui/MainPage'));
const RulesPage = lazy(() => import('@/pages/RulesPage/ui/RulesPage'));
const RulesGeneralPage = lazy(() => import('@/pages/RulesGeneralPage/ui/RulesGeneralPage'));
const RolesDetailsPage = lazy(() => import('@/pages/RolesDetailsPage/ui/RolesDetailsPage'));
const GameMasterPage = lazy(() => import('@/pages/GameMasterPage/ui/GameMasterPage'));
const EventsPage = lazy(() => import('@/pages/EventsPage/ui/EventsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage/ui/ProfilePage'));
const GoldIsPaidPage = lazy(() => import('@/pages/GoldIsPaidPage/ui/GoldIsPaidPage'));
const Leaderboard = lazy(() => import('@/pages/LeaderboardPage/ui/LeaderboardPage'));
const OtherProfilePage = lazy(() => import('@/pages/OtherProfilePage/ui/OtherProfilePage'));
const EditProfilePage = lazy(() => import('@/pages/EditProfilePage/EditProfilePage'));
const EventDetailsPage = lazy(() => import('@/pages/EventDetailsPage/ui/EventDetailsPage'));
const RolesChoisePage = lazy(() => import('@/pages/RolesChoisePage/ui/RolesChoisePage'));
const InviteFrendsPage = lazy(() => import('@/pages/InviteFrendsPage/ui/InviteFrendsPage'));
const PaidRolePage = lazy(() => import('@/pages/PaidRolePage/ui/PaidRolePage'));
const GameParticipantsPage = lazy(
  () => import('@/pages/GameParticipantsPage/ui/GameParticipantsPage'),
);
const PayBookingPage = lazy(() => import('@/pages/PayBookingPage/ui/PayBookingPage'));
const GameIsPaid = lazy(() => import('@/pages/GameIsPaidPage/ui/GameIsPaidPage'));
const ImmunityPaidPage = lazy(() => import('@/pages//ImmunityPaidPage/ui/ImmunityPaidPage'));
const BannedPage = lazy(() => import('@/pages/BannedPage/ui/BannedPage'));
const GameMasterAuthPage = lazy(() => import('@/pages/GameMasterAuthPage/ui/GameMasterAuthPage'));
const MatchResultsPage = lazy(() => import('@/pages/MatchResultsPage/ui/MatchResultsPage'));
const DevAuthPage = lazy(() => import('@/pages/DevAuthPage/ui/DevAuthPage'));
const SubscriptionPlansPage = lazy(() => import('@/pages/SubscriptionPlansPage/ui/SubscriptionPlansPage'));
const SubscriptionHistoryPage = lazy(() => import('@/pages/SubscriptionHistoryPage/ui/SubscriptionHistoryPage'));
const MigrationNoticePage = lazy(() => import('@/pages/MigrationNoticePage/ui/MigrationNoticePage'));

const RouteGuard = ({
  children,
  type,
}: {
  children: JSX.Element;
  type: 'public' | 'protected';
}) => {
  const { isAuth, status } = useAuth();
  const location = useLocation();

  if (location.pathname === '/payments/completed') {
    return children;
  }

  if (status === 'BANNED') {
    return <Navigate to="/banned" replace />;
  }

  // Migration: all authenticated users are redirected to the migration notice
  if (isAuth && location.pathname !== '/migrate') {
    return <Navigate to="/migrate" replace />;
  }

  if (type === 'protected' && !isAuth) return <Navigate to="/" replace />;
  if (type === 'public' && isAuth) return <Navigate to="/migrate" replace />;

  return children;
};

export const AppRouter = () => {
  const { initData, initDataUnsafe } = useTelegram();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const [processedPayment, setProcessedPayment] = useState<string | null>(null);

  useEffect(() => {
    import('@/pages/MainPage/ui/MainPage');
    import('@/pages/RulesPage/ui/RulesPage');
    import('@/pages/RulesGeneralPage/ui/RulesGeneralPage');
    import('@/pages/RolesDetailsPage/ui/RolesDetailsPage');
    import('@/pages/ProfilePage/ui/ProfilePage');
    import('@/pages/EventsPage/ui/EventsPage');
    import('@/pages/GoldIsPaidPage/ui/GoldIsPaidPage');
    import('@/pages/LeaderboardPage/ui/LeaderboardPage');
    import('@/pages/EditProfilePage/EditProfilePage');
    import('@/pages/EditProfilePage/EditProfilePage');
    import('@/pages/EventDetailsPage/ui/EventDetailsPage');
    import('@/pages/GameMasterPage/ui/GameMasterPage');
    import('@/pages/RolesChoisePage/ui/RolesChoisePage');
    import('@/pages/InviteFrendsPage/ui/InviteFrendsPage');
    import('@/pages/GameParticipantsPage/ui/GameParticipantsPage');
    import('@/pages/PayBookingPage/ui/PayBookingPage');
    import('@/pages/GameIsPaidPage/ui/GameIsPaidPage');
    import('@/pages/PaidBookingPage/ui/PaidBookingPage');
    import('@/pages/PaidRolePage/ui/PaidRolePage');
    import('@/pages/BannedPage/ui/BannedPage');
    import('@/pages/NotFoundPage/ui/NotFoundPage');
    import('@/pages/SubscriptionPlansPage/ui/SubscriptionPlansPage');
    import('@/pages/SubscriptionHistoryPage/ui/SubscriptionHistoryPage');
    import('@/pages/MigrationNoticePage/ui/MigrationNoticePage');
  }, []);

  useEffect(() => {
    if (initData && !isAuth) {
      dispatch(loginTelegram({ initData }));
    } else if (!initData && !isAuth && import.meta.env.DEV) {
      // В DEV режиме без Telegram — редирект на /dev-auth
      const currentPath = window.location.pathname;
      const hasTokenInUrl = new URLSearchParams(window.location.search).has('token');
      const hasTokenInStorage = !!localStorage.getItem('auth_token');
      // Не редиректим если:
      // - уже на /dev-auth
      // - есть токен в URL (обрабатывается DevAuthPage)
      // - есть токен в localStorage (авторизация в процессе или уже выполнена)
      if (currentPath !== '/dev-auth' && !hasTokenInUrl && !hasTokenInStorage) {
        navigate('/dev-auth', { replace: true });
      }
    }
  }, [initData, isAuth, dispatch, navigate]);

  useEffect(() => {
    const startParam = initDataUnsafe?.start_param;

    if (
      !startParam ||
      startParam === processedPayment ||
      (!startParam.startsWith('payment_') && !startParam.startsWith('prepayment_')) ||
      !isAuth
    ) {
      return;
    }

    const params = startParam.split('_');
    const paymentType = params[1];
    const paymentId = params[2];
    const eventId = params[3];

    const verifyPayment = async () => {
      try {
        setProcessedPayment(startParam);

        if (paymentType === 'event' && eventId) {
          const result = await dispatch(checkEventPaymentStatus(paymentId)).unwrap();
          if (result?.isConfirmed && result.eventId === eventId) {
            if (startParam.startsWith('prepayment_event')) {
              navigate(`/events/${eventId}/booking-paid`, {
                state: { paymentId, ticketsCount: result.ticketsCount },
                replace: true,
              });
            } else {
              navigate(`/events/${eventId}/game-is-paid`, {
                state: { paymentId, ticketsCount: result.ticketsCount },
                replace: true,
              });
            }
          } else {
            toast.error('Платеж не подтвержден или не завершен');
            navigate('/main', { replace: true });
          }
        } else if (paymentType === 'gold') {
          const result = await dispatch(checkGoldPaymentStatus(paymentId)).unwrap();
          if (result?.isConfirmed) {
            navigate('/gold-is-paid', { state: { paymentId }, replace: true });
          } else {
            toast.error('Платеж за золото не подтвержден');
            navigate('/main', { replace: true });
          }
        }
      } catch (error: any) {
        if (error?.payload?.code === 500) {
          toast.error('Ошибка сервера при проверке платежа');
        } else {
          toast.error('Неизвестная ошибка при проверке платежа');
        }

        navigate('/main', { replace: true });
      }
    };

    verifyPayment();
  }, [initDataUnsafe, dispatch, navigate, isAuth, processedPayment]);

  useEffect(() => {
    const startParam = initDataUnsafe?.start_param;

    if (
      !startParam ||
      !startParam.startsWith('match_results_') ||
      !isAuth
    ) {
      return;
    }

    const params = startParam.split('_');
    const matchId = params[2];

    if (matchId) {
      navigate(`/match-results/${matchId}`, { replace: true });
    }
  }, [initDataUnsafe, navigate, isAuth]);

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route
            index
            element={
              <RouteGuard type="public">
                <RegistrationPage />
              </RouteGuard>
            }
          />
          <Route
            path="/banned"
            element={
              <RouteGuard type="public">
                <BannedPage />
              </RouteGuard>
            }
          />
          <Route
            path="migrate"
            element={isAuth ? <MigrationNoticePage /> : <Navigate to="/" replace />}
          />
          {import.meta.env.DEV && (
            <Route path="/dev-auth" element={<DevAuthPage />} />
          )}

          <Route
            path="main"
            element={
              <RouteGuard type="protected">
                <MainPage />
              </RouteGuard>
            }
          />
          <Route
            path="leaderboard"
            element={
              <RouteGuard type="protected">
                <Leaderboard />
              </RouteGuard>
            }
          />
          <Route
            path="rules"
            element={
              <RouteGuard type="protected">
                <RulesPage />
              </RouteGuard>
            }
          />
          <Route
            path="rules/general"
            element={
              <RouteGuard type="protected">
                <RulesGeneralPage />
              </RouteGuard>
            }
          />
          <Route
            path="rules/roles/:roleId"
            element={
              <RouteGuard type="protected">
                <RolesDetailsPage />
              </RouteGuard>
            }
          />
          <Route
            path="other-profile/:playerId"
            element={
              <RouteGuard type="protected">
                <OtherProfilePage />
              </RouteGuard>
            }
          />

          <Route
            path="events"
            element={
              <RouteGuard type="protected">
                <EventsPage />
              </RouteGuard>
            }
          />
          <Route
            path="game-masters/:id"
            element={
              <RouteGuard type="protected">
                <GameMasterPage />
              </RouteGuard>
            }
          />
          <Route
            path="game-master-auth"
            element={
              <RouteGuard type="protected">
                <GameMasterAuthPage />
              </RouteGuard>
            }
          />
          <Route
            path="profile"
            element={
              <RouteGuard type="protected">
                <ProfilePage />
              </RouteGuard>
            }
          />
          <Route
            path="gold-is-paid"
            element={
              <RouteGuard type="protected">
                <GoldIsPaidPage />
              </RouteGuard>
            }
          />
          <Route
            path="immunity-is-paid"
            element={
              <RouteGuard type="protected">
                <ImmunityPaidPage />
              </RouteGuard>
            }
          />

          <Route
            path="role-is-paid"
            element={
              <RouteGuard type="protected">
                <PaidRolePage />
              </RouteGuard>
            }
          />
          <Route
            path="edit-profile"
            element={
              <RouteGuard type="protected">
                <EditProfilePage />
              </RouteGuard>
            }
          />
          <Route
            path="match-results/:matchId"
            element={
              <RouteGuard type="protected">
                <MatchResultsPage />
              </RouteGuard>
            }
          />
          <Route
            path="events/:id"
            element={
              <RouteGuard type="protected">
                <EventDetailsPage />
              </RouteGuard>
            }
          />
          <Route
            path="events/:id/choise-role"
            element={
              <RouteGuard type="protected">
                <RolesChoisePage />
              </RouteGuard>
            }
          />
          <Route
            path="events/:id/game-participants"
            element={
              <RouteGuard type="protected">
                <GameParticipantsPage />
              </RouteGuard>
            }
          />
          <Route
            path="events/:id/pay-booking"
            element={
              <RouteGuard type="protected">
                <PayBookingPage />
              </RouteGuard>
            }
          />
          <Route
            path="events/:id/game-is-paid"
            element={
              <RouteGuard type="protected">
                <GameIsPaid />
              </RouteGuard>
            }
          />
          <Route
            path="/events/:id/booking-paid"
            element={
              <RouteGuard type="protected">
                <BookingIsPaidPage />
              </RouteGuard>
            }
          />

          <Route
            path="invite-frends"
            element={
              <RouteGuard type="protected">
                <InviteFrendsPage />
              </RouteGuard>
            }
          />
          <Route
            path="subscriptions"
            element={
              <RouteGuard type="protected">
                <SubscriptionPlansPage />
              </RouteGuard>
            }
          />
          <Route
            path="subscriptions/history"
            element={
              <RouteGuard type="protected">
                <SubscriptionHistoryPage />
              </RouteGuard>
            }
          />
          <Route path="*" element={<Navigate to="/main" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};
